'use client';

import { LoaderCircle } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { BreadcrumbList } from '@/features/breadcrumbs';
import {
	type CandidateActionResult,
	createViewTracker,
	getCandidateAttemptAction,
	getCandidateSelectionAction,
	hasInterviewCards,
	listCandidateAttemptsAction,
	mergeAttemptHistory,
	resultHref,
	selectionErrorMessage,
	selectionErrors,
	selectionPending,
	selectionRequestKey,
	startCandidateInterviewAction,
	unwrapCandidateResult,
	useCandidateAnalytics,
	usesReportOverview,
} from '@/features/candidate-interview';
import { OnboardingButton } from '@/features/onboarding-survey';
import type {
	CandidateInterviewHistoryItemV1,
	CandidateInterviewHistoryV1,
	CandidateInterviewV1,
	CandidateSelectionStatusV1,
} from '@/shared/api';
import { Button } from '@/shared/ui/button';
import {
	CatalogCard,
	catalogCardDescriptionVariants,
	catalogCardTitleClass,
} from '@/shared/ui/catalog-card';
import { Container } from '@/shared/ui/container';
import { WorkspaceContainer } from '@/shared/ui/workspace-container';
import { declension } from '@/shared/utils';

import { CandidateHistoryRow } from './candidate-history';
import { CandidateMessage } from './candidate-message';
import { CandidateOverviewActions } from './candidate-overview-actions';
import { CandidateOverviewHero } from './candidate-overview-hero';

type Props = {
	interview: CandidateInterviewV1;
	initialHistory: CandidateActionResult<CandidateInterviewHistoryV1>;
	selectedAttempt?: CandidateInterviewHistoryItemV1;
	selectedAttemptId?: string;
	languageLabel?: string;
	startAction?: typeof startCandidateInterviewAction;
	autoStart?: boolean;
	onAttemptReady?: (attempt: { id: string; status: string }) => void;
};

export function CandidateInterviewScreen({
	interview,
	initialHistory,
	selectedAttempt,
	selectedAttemptId,
	languageLabel,
	startAction = startCandidateInterviewAction,
	onAttemptReady,
	autoStart = false,
}: Props) {
	const workspace = usesReportOverview(interview);
	const PageContainer = workspace ? WorkspaceContainer : Container;
	const router = useRouter();
	const track = useCandidateAnalytics(
		interview.id,
		selectedAttemptId ? 'result' : 'landing',
	);
	const seen = useRef(createViewTracker());
	const [items, setItems] = useState(initialHistory.data?.items ?? []);
	const [total, setTotal] = useState(initialHistory.data?.total ?? 0);
	const [historyError, setHistoryError] = useState(initialHistory.error ?? '');
	const [error, setError] = useState(
		interview.selection?.status === 'error'
			? selectionErrorMessage(interview.selection.errorCode)
			: '',
	);
	const [selection, setSelection] = useState<
		CandidateSelectionStatusV1 | undefined
	>(
		interview.selection?.status === 'completed'
			? undefined
			: interview.selection,
	);
	const [pollFailed, setPollFailed] = useState(false);
	const [pollRevision, setPollRevision] = useState(0);
	const requestId = useRef<string | undefined>(undefined);
	const requestStorageKey = `candidate-selection:${interview.id}`;
	const clearRequest = useCallback(() => {
		requestId.current = undefined;
		try {
			sessionStorage.removeItem(requestStorageKey);
		} catch {}
	}, [requestStorageKey]);
	const [busy, setBusy] = useState(false);
	const [historyBusy, setHistoryBusy] = useState(false);
	const [historyExpansion, setHistoryExpansion] = useState({
		route: selectedAttemptId,
		openId: selectedAttemptId?.toLowerCase(),
	});
	if (historyExpansion.route !== selectedAttemptId) {
		setHistoryExpansion({
			route: selectedAttemptId,
			openId: selectedAttemptId?.toLowerCase(),
		});
	}
	const openId =
		historyExpansion.route === selectedAttemptId
			? historyExpansion.openId
			: selectedAttemptId?.toLowerCase();
	const inFlight = useRef(false);
	const autoStarted = useRef(false);
	const historyFlight = useRef(false);
	const breadcrumbs = useMemo(
		() => [
			{
				title: workspace ? 'Все курсы' : 'Все задачи',
				href: workspace ? '/courses' : '/problems',
			},
			{ title: interview.title },
		],
		[interview.title, workspace],
	);
	const active =
		interview.attempt?.status === 'in_progress' ? interview.attempt : undefined;
	const minutes = Math.ceil(
		(interview.targetDurationSeconds ?? interview.totalSeconds) / 60,
	);
	const pending = selectionPending(selection);
	const canStart = interview.canStart || selection?.status === 'error';
	const displayedItems =
		selectedAttempt &&
		!items.some(
			(item) => item.id.toLowerCase() === selectedAttempt.id.toLowerCase(),
		)
			? [...items, selectedAttempt]
			: items;
	const historyCount = Math.max(total, displayedItems.length);
	useEffect(() => {
		if (seen.current('page'))
			track('page_view', {
				interviewType: interview.type,
				hasHistory: historyCount > 0,
				hasActiveAttempt: !!active,
			});
		if (initialHistory.error && seen.current('history_error'))
			track('request_error', { action: 'history', outcome: 'request_failed' });
	}, [track, interview.type, historyCount, active, initialHistory.error]);
	const loadHistory = useCallback(
		async (more: boolean) => {
			if (historyFlight.current) return;
			historyFlight.current = true;
			setHistoryBusy(true);
			track(more ? 'history_more' : 'retry', { action: 'history' });
			try {
				const next = unwrapCandidateResult(
					await listCandidateAttemptsAction(
						interview.id,
						more ? items.length : 0,
					),
				);
				setItems((previous) =>
					more ? mergeAttemptHistory(previous, next.items) : next.items,
				);
				setTotal(next.total);
				if (historyError) track('recovery', { action: 'history' });
				setHistoryError('');
			} catch {
				setHistoryError('Не удалось загрузить историю собеседований.');
				track('request_error', {
					action: 'history',
					outcome: 'request_failed',
				});
			} finally {
				historyFlight.current = false;
				setHistoryBusy(false);
			}
		},
		[interview.id, items.length, historyError, track],
	);
	const goToAttempt = useCallback(
		(attempt: { id: string; status: string }) => {
			clearRequest();
			if (onAttemptReady) {
				onAttemptReady(attempt);
				return;
			}
			const result = resultHref(interview.id, attempt);
			router.push(
				result ?? `/interviews/${encodeURIComponent(interview.id)}/session`,
			);
		},
		[clearRequest, interview.id, router, onAttemptReady],
	);
	useEffect(() => {
		if (selection?.status === 'error') clearRequest();
		if (!selectionPending(selection) || !selection) return;
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const id = selection.selectionId;
		if (seen.current(`selection_pending:${id}`)) track('selection_pending');
		const poll = async () => {
			try {
				const result = await getCandidateSelectionAction(id);
				if (cancelled) return;
				if (result.error !== null) throw new Error(result.error);
				const state = result.data;
				if (state.status === 'error') {
					clearRequest();
					setSelection(state);
					setBusy(false);
					inFlight.current = false;
					setError(selectionErrorMessage(state.errorCode));
					setPollFailed(false);
					if (seen.current(`selection_terminal:${id}`))
						track('selection_error', {
							outcome:
								state.errorCode &&
								Object.hasOwn(selectionErrors, state.errorCode)
									? state.errorCode
									: 'selection_failed',
						});
					return;
				}
				if (state.status === 'completed' && state.attemptId) {
					const attempt = await getCandidateAttemptAction(state.attemptId);
					if (cancelled) return;
					if (attempt.error !== null) throw new Error(attempt.error);
					if (seen.current(`selection_terminal:${id}`)) {
						track('selection_success');
						track('start_success');
					}
					goToAttempt(attempt.data);
					return;
				}
				timer = setTimeout(poll, 1500);
			} catch {
				if (cancelled) return;
				setPollFailed(true);
				setError('Не удалось проверить статус подбора. Проверьте соединение.');
				if (seen.current(`selection_poll_error:${id}:${pollRevision}`))
					track('request_error', {
						action: 'selection_status',
						outcome: 'request_failed',
					});
			}
		};
		void poll();
		return () => {
			cancelled = true;
			if (timer) clearTimeout(timer);
		};
	}, [selection, pollRevision, track, clearRequest, goToAttempt]);
	const start = useCallback(
		async (automatic = false) => {
			if (inFlight.current || pending || (!active && !canStart)) return;
			inFlight.current = true;
			setBusy(true);
			setError('');
			setPollFailed(false);
			track(
				active ? 'continue_intent' : 'start_intent',
				automatic ? { action: 'autostart' } : undefined,
			);
			try {
				let stored: Storage | undefined;
				try {
					stored = sessionStorage;
				} catch {}
				const key = active
					? undefined
					: selectionRequestKey(
							stored,
							requestStorageKey,
							requestId.current,
							() => crypto.randomUUID(),
						);
				requestId.current = key;
				const result = await (active
					? getCandidateAttemptAction(active.id)
					: startAction(interview.id, interview.languageId, key));
				if (result.error !== null) {
					if (result.code) clearRequest();
					setError(result.error);
					track('request_error', {
						action: automatic ? 'autostart' : active ? 'continue' : 'start',
						outcome: result.code ?? 'request_failed',
					});
					inFlight.current = false;
					setBusy(false);
					return;
				}
				const next = result.data;
				if ('selectionId' in next) {
					setSelection(next);
					return;
				}
				track(
					active ? 'continue_success' : 'start_success',
					automatic ? { action: 'autostart' } : undefined,
				);
				goToAttempt(next);
			} catch {
				setError(
					active
						? 'Не удалось продолжить интервью. Попробуйте ещё раз.'
						: 'Не удалось получить ответ на запуск. Нажмите «Начать», чтобы проверить тот же запрос.',
				);
				track('request_error', {
					action: automatic ? 'autostart' : active ? 'continue' : 'start',
					outcome: 'request_failed',
				});
				inFlight.current = false;
				setBusy(false);
			}
		},
		[
			active,
			canStart,
			clearRequest,
			goToAttempt,
			interview.id,
			interview.languageId,
			pending,
			requestStorageKey,
			startAction,
			track,
		],
	);
	useEffect(() => {
		if (!autoStart || autoStarted.current) return;
		autoStarted.current = true;
		if (pending || (!active && (!canStart || interview.attempt))) return;
		void start(true);
	}, [autoStart, active, canStart, interview.attempt, pending, start]);
	const action = active?.action ?? interview.action;
	if (!hasInterviewCards(action))
		return (
			<CandidateMessage
				illustration="start"
				title={interview.title}
				text={
					<>
						<p>
							Интервью рассчитано на {minutes}{' '}
							{declension(minutes, ['минуту', 'минуты', 'минут'])}, содержит{' '}
							{interview.itemCount}{' '}
							{declension(interview.itemCount, [
								'задание',
								'задания',
								'заданий',
							])}
							{languageLabel && ` · ${languageLabel}`}
						</p>
						<p>
							У каждого задания свой таймер, по истечении которого произойдет
							автоматический переход к следующей задаче.
						</p>
						<p>Учитывай, что вернуться к предыдущим задачам нельзя.</p>
					</>
				}
			>
				<OnboardingButton
					className="min-w-40"
					onClick={() => void start()}
					loading={busy || pending}
					disabled={!active && !canStart}
				>
					{active ? 'Продолжить' : 'Начать'}
				</OnboardingButton>
				{error && (
					<p role="alert" className="mt-4 text-red">
						{error}
					</p>
				)}
			</CandidateMessage>
		);
	return (
		<main
			id={workspace ? 'main' : undefined}
			className={
				workspace
					? 'min-w-0 pb-16 pt-6 text-black sm:pt-8'
					: 'h-full min-w-0 overflow-y-auto bg-gray-5 pb-14 pt-8 text-black sm:pt-10'
			}
		>
			<PageContainer {...(!workspace ? { fit: true } : {})}>
				<div
					className="mb-3"
					onClickCapture={(event) => {
						if (
							event.target instanceof Element &&
							event.target.closest('a')?.getAttribute('href') ===
								(workspace ? '/courses' : '/problems')
						)
							track('catalog_exit');
					}}
				>
					<BreadcrumbList list={breadcrumbs} />
				</div>
				{workspace ? (
					<>
						<CandidateOverviewHero
							title={interview.title}
							description={`Интервью с AI: ${interview.itemCount} ${declension(interview.itemCount, ['задача', 'задачи', 'задач'])}, ${minutes} ${declension(minutes, ['минута', 'минуты', 'минут'])}${languageLabel ? ` · ${languageLabel}` : ''}. У каждого задания свой таймер. Когда время закончится, начнётся следующая задача. Вернуться к предыдущим задачам нельзя. После завершения здесь появится отчёт с разбором решений.`}
							onEvent={track}
						/>
						<CandidateOverviewActions
							minutes={minutes}
							active={!!active}
							pending={pending}
							busy={busy}
							canStart={canStart}
							onStart={() => void start()}
							onCareer={() => track('career_offer_click')}
						/>
						{pollFailed && (
							<button
								type="button"
								className="mt-3 rounded underline"
								onClick={() => {
									track('selection_status_retry');
									setPollFailed(false);
									setError('');
									setPollRevision((value) => value + 1);
								}}
							>
								Проверить статус
							</button>
						)}
					</>
				) : (
					<>
						<h1 className="mb-7 text-3xl font-bold sm:mb-8">
							{interview.title}
						</h1>
						<div className="grid min-w-0 gap-4 md:grid-cols-2">
							<section className="min-w-0">
								<CatalogCard variant="aurora" className={'px-2 md:px-6'}>
									<div className="relative z-10 flex h-full flex-col items-start">
										<h2 className={catalogCardTitleClass}>
											Собеседование с AI
										</h2>
										<p
											className={catalogCardDescriptionVariants({
												variant: 'aurora',
											})}
										>
											{interview.itemCount}{' '}
											{declension(interview.itemCount, [
												'задача',
												'задачи',
												'задач',
											])}{' '}
											· {minutes}{' '}
											{declension(minutes, ['минута', 'минуты', 'минут'])}
											{languageLabel && ` · ${languageLabel}`}
										</p>
										<Button
											type="button"
											variant="white"
											size="xs"
											disabled={busy || pending || (!active && !canStart)}
											className="mt-6 shrink-0"
											onClick={() => void start()}
										>
											{(busy || pending) && (
												<LoaderCircle
													size={18}
													className="animate-spin motion-reduce:animate-none"
													aria-hidden="true"
												/>
											)}
											{pending
												? 'Подбираем задачи…'
												: active
													? 'Продолжить'
													: 'Начать'}
										</Button>
										{pollFailed && (
											<button
												type="button"
												className="mt-3 underline"
												onClick={() => {
													track('selection_status_retry');
													setPollFailed(false);
													setError('');
													setPollRevision((value) => value + 1);
												}}
											>
												Проверить статус
											</button>
										)}
										{!active && !canStart && !pending && (
											<p className="mt-3 text-sm text-green-100">
												Это собеседование уже пройдено.
											</p>
										)}
									</div>
								</CatalogCard>
							</section>
							<section className="min-w-0">
								<CatalogCard variant="emerald" className={'px-2 md:px-6'}>
									<div className="relative z-10 flex h-full flex-col items-start">
										<h2 className={catalogCardTitleClass}>
											Оффер в Яндекс под ключ
										</h2>
										<p
											className={catalogCardDescriptionVariants({
												variant: 'emerald',
											})}
										>
											Сопровождение до оффера
										</p>
										<Button
											variant="white"
											size="xs"
											href="/career-offer"
											onClick={() => track('career_offer_click')}
											className="mt-6 shrink-0"
										>
											Подробнее
										</Button>
									</div>
								</CatalogCard>
							</section>
						</div>
					</>
				)}

				{error && (
					<div
						role="alert"
						className="mt-4 rounded-xl bg-red-50 p-4 text-red-800"
					>
						{error}{' '}
						<button
							type="button"
							className="rounded underline focus-visible:ring-2 focus-visible:ring-red-500"
							disabled={busy}
							onClick={() => {
								track('retry', { action: active ? 'continue' : 'start' });
								void start();
							}}
						>
							Повторить
						</button>
					</div>
				)}
				{historyError && (
					<div
						role="alert"
						className="mt-7 rounded-xl bg-white p-5 text-gray-600"
					>
						{historyError}{' '}
						<button
							type="button"
							className="rounded underline focus-visible:ring-2 focus-visible:ring-green-600"
							disabled={historyBusy}
							onClick={() => void loadHistory(false)}
						>
							Обновить
						</button>
					</div>
				)}
				{displayedItems.length > 0 && (
					<section
						className={workspace ? 'mt-9 sm:mt-10' : 'mt-10 sm:mt-11'}
						aria-labelledby="candidate-history-title"
					>
						<div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
							<h2
								id="candidate-history-title"
								className={
									workspace
										? 'text-lg font-semibold tracking-tight'
										: 'text-3xl font-bold'
								}
							>
								История собеседований
							</h2>
						</div>
						<div className="space-y-3">
							{displayedItems.map((attempt) => (
								<CandidateHistoryRow
									key={attempt.id}
									variant={workspace ? 'workspace' : 'legacy'}
									interviewId={interview.id}
									attempt={attempt}
									open={openId === attempt.id.toLowerCase()}
									onToggle={() => {
										const opening = openId !== attempt.id.toLowerCase();
										setHistoryExpansion({
											route: selectedAttemptId,
											openId: opening ? attempt.id.toLowerCase() : undefined,
										});
										if (!opening)
											track('report_close', {
												reportStatus: attempt.reportStatus,
											});
									}}
									onEvent={track}
								/>
							))}
						</div>
						{items.length < total && (
							<div className="mt-6 text-center">
								<button
									type="button"
									disabled={historyBusy}
									onClick={() => void loadHistory(true)}
									className="rounded-xl bg-white px-6 py-3 font-medium hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-green-600 disabled:opacity-60"
								>
									{historyBusy ? 'Загружаем…' : 'Показать ещё'}
								</button>
							</div>
						)}
					</section>
				)}
			</PageContainer>
		</main>
	);
}
